<template>
    <div class="listing-layout flex-column pb-8">
        <!-- LOADER -->
        <preloader :display="loading" class="preloader prelative" :background="false" />

        <!-- HEADER -->
        <header class="flex-level mb-4 mt-1">
            <!-- TITLE -->
            <h1 class="m-0">{{ $l10n.__('Planification de rendez-vous') }}</h1>
        </header>

        <!-- BODY -->
        <div class="flex">
            <div class="container-medium m-auto flex">
                <el-collapse class="collapse-onboarding-mail wp-9 m-auto" :modelValue="step">
                    <el-collapse-item :name="1" disabled>
                        <template #title>
                            1/ {{ $l10n.__('Connectez votre compte') }}
                            <span v-if="provider" class="fs-3 regular">{{ provider.title }}</span>
                        </template>

                        <!-- Only Emails are already sync -->
                        <template v-if="hasOnlyEmail">
                            <div class="onboarding-scheduler-disclaimer mx-auto mb-5">
                                <p class="gray-60 semibold mb-2">
                                    {{ $filters.capitalize(providerName(emailProvider.provider)) }}
                                </p>
                                <p class="mt-0 mb-4">
                                    {{
                                        $l10n.__(
                                            "Vous avez déjà synchronisé un compte pour le module email, afin d'utiliser la fonctionnalité de planification de rendez-vous, vous devez vous connecter avec ce même compte.",
                                        )
                                    }}
                                </p>
                                <onboarding-provider
                                    :provider="emailProvider.provider"
                                    :user="emailProvider.email"
                                    @callback="
                                        validSync(emailProvider.provider, emailProvider.email)
                                    "
                                ></onboarding-provider>
                                <div
                                    v-if="needToUpdateScope(emailProvider.provider)"
                                    class="br primary-05-bkg mt-2 px-3 onboarding-alert"
                                >
                                    <span>{{
                                        $l10n.__(
                                            "À la prochaine étape vous devrez autoriser l'accès aux permissions d'agenda.",
                                        )
                                    }}</span>
                                </div>
                            </div>
                        </template>

                        <!-- Only Calendar are already sync -->
                        <template v-else-if="hasOnlySync">
                            <div class="onboarding-scheduler-disclaimer mx-auto mb-5">
                                <p class="gray-60 semibold">
                                    {{
                                        $filters.capitalize(providerName(calendarProvider.provider))
                                    }}
                                </p>
                                <p>
                                    {{
                                        $l10n.__(
                                            "Vous avez déjà synchronisé un compte pour le module %s, afin d'utiliser la fonctionnalité de planification de rendez-vous, vous devez vous connecter avec ce même compte.",
                                            providerName(calendarProvider.provider),
                                        )
                                    }}
                                </p>
                                <onboarding-provider
                                    :provider="calendarProvider.provider"
                                    :user="calendarProvider.email"
                                    @callback="
                                        validSync(calendarProvider.provider, calendarProvider.email)
                                    "
                                ></onboarding-provider>
                                <div
                                    v-if="needToUpdateScope(calendarProvider.provider)"
                                    class="br primary-05-bkg mt-2 px-3 onboarding-alert"
                                >
                                    <span>{{
                                        $l10n.__(
                                            "À la prochaine étape vous devrez autoriser l'accès aux permissions d'agenda.",
                                        )
                                    }}</span>
                                </div>
                            </div>
                        </template>

                        <!-- Different emails are sync -->
                        <template v-else-if="hasDifferentSync">
                            <div class="onboarding-scheduler-disclaimer mx-auto mb-5">
                                <p class="gray-60 semibold">
                                    {{ providerName(calendarProvider.provider) }}
                                </p>
                                <card
                                    background="warning-05"
                                    :padding="2"
                                    class="b-1-warning-50 px-3"
                                >
                                    <span>{{
                                        $l10n.__(
                                            'Vous avez synchronisé un compte différent sur le module %(providerName)s. Afin de voir les événements créés depuis le module planification de rendez-vous, nous vous conseillons de modifier le compte connecté sur le module %(providerName)s.',
                                            {
                                                providerName: providerName(
                                                    calendarProvider.provider,
                                                ),
                                            },
                                        )
                                    }}</span>
                                </card>
                                <onboarding-provider
                                    :provider="calendarProvider.provider"
                                    :user="calendarProvider.email"
                                    :ctaLabel="$t.__('common.actions.edit')"
                                    class="mt-2"
                                    @callback="loadPrefs(calendarProvider.provider)"
                                ></onboarding-provider>

                                <p class="gray-60 mt-7 semibold">Module email</p>
                                <p>
                                    {{
                                        $l10n.__(
                                            "Vous avez déjà synchronisé un compte pour le module email, afin d'utiliser la fonctionnalité de planification de rendez-vous, vous devez vous connecter avec ce même compte.",
                                        )
                                    }}
                                </p>
                                <onboarding-provider
                                    :provider="emailProvider.provider"
                                    :user="emailProvider.email"
                                    @callback="
                                        validSync(emailProvider.provider, emailProvider.email)
                                    "
                                ></onboarding-provider>
                                <div
                                    v-if="needToUpdateScope(emailProvider.provider)"
                                    class="br primary-05-bkg mt-2 px-3 onboarding-alert"
                                >
                                    <span>{{
                                        $l10n.__(
                                            "À la prochaine étape vous devrez autoriser l'accès aux permissions d'agenda.",
                                        )
                                    }}</span>
                                </div>
                            </div>
                        </template>

                        <!-- No sync at all -->
                        <template v-else>
                            <div class="onboarding-scheduler-disclaimer mx-auto mb-5">
                                <card
                                    v-if="hasTransfertMode"
                                    background="warning-05"
                                    :padding="2"
                                    class="mt-3 b-1-warning-50 px-3"
                                >
                                    <span>{{
                                        $l10n.__(
                                            'Le mode transfert du module email ne vous permet pas d’utiliser la fonctionnalité de planification de rendez-vous. Vous devez utiliser le module email en mode synchronisation.',
                                        )
                                    }}</span>
                                </card>
                                <p v-else class="semibold gray-60 m-0">
                                    {{
                                        $l10n.__(
                                            'Pour utiliser la fonctionnalité de planification de rendez-vous, vous devez utiliser le module email en mode synchronisation.',
                                        )
                                    }}
                                </p>
                                <div class="br primary-05-bkg mt-2 px-3 onboarding-alert">
                                    <span>{{
                                        $l10n.__(
                                            'Une fois le service sélectionné vous serez automatiquement redirigé vers le module email en mode synchronisation',
                                        )
                                    }}</span>
                                </div>
                            </div>
                            <email-onboarding-provider @validProvider="onValidProvider">
                                <template #subtitle>
                                    <p class="subtitle fs-3 m-0">
                                        {{
                                            $l10n.__(
                                                'Pour profiter pleinement de la planification de rendez-vous dans Sellsy, sélectionnez le service que vous souhaitez utiliser.',
                                            )
                                        }}
                                    </p>
                                </template>
                            </email-onboarding-provider>
                        </template>
                        <template v-if="otherProviderError">
                            <div class="flex-level jc-center mt-3">
                                <span
                                    class="br warning-05-bkg b-1-warning-50 px-3 onboarding-alert"
                                    >{{
                                        $l10n.__(
                                            'Malheureusement la fonctionnalité n’est pas encore disponible sur Sellsy.',
                                        )
                                    }}</span
                                >
                            </div>
                            <router-link :to="{ name: 'dashboard' }">
                                <CompassButton
                                    :label="
                                        $t.__(
                                            'pages.onboarding.scheduler.OnboardingScheduler.back_to_agenda',
                                        )
                                    "
                                    type="primary"
                                    class="mt-5 mx-auto block onboarding-back"
                                />
                            </router-link>
                        </template>
                    </el-collapse-item>
                </el-collapse>
            </div>
        </div>
    </div>
</template>

<script>
import Preloader from 'vcomponents/utilities/Preloader.vue';
import EmailOnboardingProvider from 'vcomponents/email/EmailOnboardingProvider.vue';
import EmailOnboardingMode from 'vcomponents/email/EmailOnboardingMode.vue';
import EmailOnboardingDomain from 'vcomponents/email/EmailOnboardingDomain.vue';
import OnboardingEmailApi from 'api/onboarding/email/onboardingEmail';
import { getSchedulerMetadata } from 'api/onboarding/scheduler/onboardingScheduler';
import Card from 'vcomponents/Card.vue';
import { goToUrl } from 'js/vue/helpers';
import OnboardingProvider from 'vcomponents/pages/onboarding/scheduler/OnboardingProvider.vue';

export default {
    components: {
        Preloader,
        EmailOnboardingProvider,
        EmailOnboardingMode,
        EmailOnboardingDomain,
        Card,
        OnboardingProvider,
    },
    data() {
        return {
            loading: false,
            step: null,
            provider: null,
            mode: 'sync',
            domain: null,
            params: null,
            otherProviderError: false,
            emailProvider: null,
            calendarProvider: null,
            schedulerAlreadyConfigured: false,
        };
    },
    computed: {
        hasTransfertMode() {
            return this.emailProvider?.mode === 'transfer';
        },
        hasEmailProvider() {
            return this.emailProvider?.mode === 'sync' && !!this.emailProvider?.email;
        },
        hasSyncProvider() {
            return !!this.calendarProvider?.email;
        },
        hasOnlySync() {
            return !this.hasEmailProvider && this.hasSyncProvider;
        },
        hasOnlyEmail() {
            return this.hasEmailProvider && !this.hasSyncProvider;
        },
        hasDifferentSync() {
            return (
                this.hasEmailProvider &&
                this.hasSyncProvider &&
                this.emailProvider?.email.toLowerCase() !==
                    this.calendarProvider?.email.toLowerCase()
            );
        },
    },
    mounted() {
        document.getElementById('sellsy_app').classList.add('white-bkg');
    },
    /**
     * On created component,
     *  - Add specific class on body element
     *  - Call legacy back for init onboarding email (in bdd reset onboarding_setup to 'N')
     *    then initOnboardingEmailStorage and if provider exists, call method "onValidProvider" for pass directly to step 2
     */
    created() {
        document.body.className = '';
        document.body.classList.add('page-onboarding-scheduler');
        this.initOnboarding();
    },
    beforeUnmount() {
        document.getElementById('sellsy_app').classList.remove('white-bkg');
    },
    /**
     * On destroyed component,
     *  - Delete specific class on body element
     *
     */
    unmounted() {
        document.body.classList.remove('page-onboarding-email');
    },
    methods: {
        initOnboarding() {
            getSchedulerMetadata().then((res) => {
                this.step = 1;
                this.emailProvider = res.email;
                this.calendarProvider = res.calendar;
                if (this.$attrs.provider) this.onValidProvider(this.$attrs.provider);
            });
        },
        /**
         * When user valid the choice of the provider
         * set provider and pass to step 2
         *
         * @param {object} provider
         * @return {void}
         */
        onValidProvider(provider) {
            if (provider.id === 'other') {
                this.otherProviderError = true;
                return;
            }
            OnboardingEmailApi.validateOnboardingEmailStep(2, {
                provider: provider.id,
                successUrl: this.$router.resolve({ name: 'onboarding.scheduler.confirmed' }).href,
            })
                .then(({ onboarding_params }) => {
                    this.params = onboarding_params;
                    this.provider = provider;
                    this.onValidMode();
                })
                .catch((err) => {
                    Push.error(this.$l10n.__('Une erreur est survenue'), false);
                });
        },
        /**
         * When user valid the choice of the mode
         * performs actions according to the mode an provider
         *
         * @return {void}
         */
        onValidMode() {
            switch (`${this.mode}::${this.provider.id}`) {
                case 'sync::gmail':
                    window.location.href = `?_f=prefsNylas&action=oauthGoogle&successUrl=${
                        this.$router.resolve({ name: 'onboarding.scheduler.confirmed' }).href
                    }`;
                    break;
                case 'sync::office365':
                    window.location.href = `?_f=prefsNylas&action=oauthMicrosoftOffice365&successUrl=${
                        this.$router.resolve({ name: 'onboarding.scheduler.confirmed' }).href
                    }`;
                    break;
                default:
                    break;
            }
        },

        /**
         * Valid Sync with current provider
         * @param {string} provider : provider id
         * @param {string} email : email to sync
         */
        validSync(provider, email) {
            switch (provider) {
                case 'gmail':
                case 'google':
                    window.location.href = `/?_f=prefsNylas&action=oauthGoogle&login_hint=${email}&successUrl=${
                        this.$router.resolve({ name: 'onboarding.scheduler.confirmed' }).href
                    }`;
                    break;
                case 'office365':
                case 'outlook':
                    window.location.href = `/?_f=prefsNylas&action=oauthMicrosoftOffice365&successUrl=${
                        this.$router.resolve({ name: 'onboarding.scheduler.confirmed' }).href
                    }`;
                    break;
            }
        },
        /**
         * Redirect to onboarding email final page
         *
         * @return {void}
         */
        goToFinalPage() {
            this.$router.push({ name: 'onboarding.scheduler.confirmed' });
        },

        /**
         * Load Modals Prefs for current provider
         * @param {string} provider : provider name
         * @return {void}
         */
        loadPrefs(provider) {
            switch (provider) {
                case 'gmail':
                case 'google':
                    GoogleSync.loadPrefs();
                    break;
                case 'outlook':
                case 'office365':
                    OutlookSync.loadPrefs('scheduler');
                    break;
                default:
                    break;
            }
        },

        /**
         * Check if provider need to update scopes
         * @param {string} provider
         * @return {boolean}
         */
        needToUpdateScope(provider) {
            return ['gmail', 'google'].includes(provider);
        },

        /**
         * Get Provider name formatted
         * @param {string} provider
         * @return {string}
         */
        providerName(provider) {
            switch (provider) {
                case 'gmail':
                    return 'Gmail';
                case 'google':
                    return 'Google Sync';
                case 'outlook':
                case 'office365':
                    return 'Office 365';
            }
        },
        goToAgenda() {
            goToUrl('/?_f=agenda');
        },
    },
};
</script>

<style lang="scss">
@use '~@sellsy/styleguide/src/_variables' as *;
.el-collapse.collapse-onboarding-mail {
    width: 976px;
    border-top: none;
    border-bottom: none;
    .el-dialog .el-dialog__header {
        .el-dialog__title {
            font-size: 20px;
            line-height: 24px;
        }
    }

    .onboarding-alert,
    .onboarding-back {
        padding-top: 5px;
        padding-bottom: 5px;

        &.primary-05-bkg {
            border: 1px solid $primary-10;
        }
    }

    .el-collapse-item__header,
    .el-collapse-item__wrap,
    .el-collapse-item__content {
        background-color: transparent;
    }

    .el-collapse-item__arrow {
        display: none;
    }

    .el-collapse-item__header {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        color: $gray-60;
        height: 60px;
        padding-bottom: 5px;
        font-size: 17px;
        font-weight: bold;
        letter-spacing: 0;
        line-height: 21px;

        &.is-active {
            color: $gray-100;
            border-bottom: 1px solid $gray-05;
        }
    }

    .el-collapse-item.is-disabled {
        .el-collapse-item__header {
            cursor: default;
        }
    }

    .el-collapse-item__wrap {
        border-bottom: transparent;
    }

    .el-collapse-item__content {
        margin: 40px auto 40px auto;
    }

    .onboarding-scheduler-disclaimer {
        width: 560px;
    }
}
</style>
